import { useEffect, useState, type CSSProperties } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import type { AcessoDaHabilitacao, HabilitacaoComProvas, Me, Prontidao, Prova, TentativaAberta } from "../../types"
import { cronometro, dataCurta, reais } from "../../lib/format"
import { ROTULO, fraseDeProntidao } from "../../lib/prontidao"
import { Barra, Botao, Carregando, Erro, Selo } from "../ui"
import Desempenho from "./Desempenho"
import Materiais from "./Materiais"
import Plano from "./Plano"
import Flashcards from "./Flashcards"

// A partir daqui o vencimento deixa de ser informação e vira chamada para ação.
// Duas semanas é o tamanho da janela de estudo curta: quem tem menos que isso
// pela frente corre risco real de perder o acesso antes da prova.
const DIAS_PARA_AVISAR = 14

export default function Painel({ me, recarregar }: { me: Me; recarregar: () => Promise<void> }) {
  const [habilitacoes, setHabilitacoes] = useState<HabilitacaoComProvas[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [iniciando, setIniciando] = useState<number | null>(null)
  const [parametros] = useSearchParams()
  const navegar = useNavigate()

  // Quem chega da landing de uma habilitação traz `?habilitacao=slug` (o
  // cadastro carrega a intenção pela sessão). Sem isso, a ordem do catálogo
  // punha o Motonauta em primeiro para todo mundo — e o candidato de Arrais
  // fazia o diagnóstico da habilitação errada. Mesmo padrão do Acesso.
  const slugPreferido = parametros.get("habilitacao")

  useEffect(() => {
    api.get<{ habilitacoes: HabilitacaoComProvas[] }>("/provas").then((r) => setHabilitacoes(r.habilitacoes))
    // O `me` chega do boot do app e pode estar velho — simulado finalizado em
    // outra aba, prazo expirado no servidor. O painel é onde isso aparece
    // ("simulado em andamento"), então revalida ao entrar.
    recarregar()
  }, [recarregar])

  async function comecar(prova: Prova) {
    setErro(null)
    setIniciando(prova.id)
    try {
      const tentativa = await api.post<TentativaAberta>("/tentativas", { prova_id: prova.id })
      navegar(`/simulado/${tentativa.id}`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.corpo.tentativa_id) {
        navegar(`/simulado/${e.corpo.tentativa_id}`)
        return
      }
      if (e instanceof ApiError && e.status === 402) {
        navegar(`/acesso?habilitacao=${prova.slug}`)
        return
      }
      setErro(e instanceof Error ? e.message : "Não foi possível iniciar o simulado.")
      await recarregar()
    } finally {
      setIniciando(null)
    }
  }

  const aberta = me.tentativa_em_andamento
  const estreante = me.tentativas_concluidas === 0

  const ordenadas = habilitacoes
    ? slugPreferido
      ? [...habilitacoes].sort((a) => (a.slug === slugPreferido ? -1 : 1))
      : habilitacoes
    : []

  return (
    <div className="space-y-9">
      {/* Cabeçalho como abertura de diário: a etiqueta em mono, o nome em
          display e a promessa embaixo, fechado por uma régua fina. */}
      {/* Para quem chega, o cabeçalho é uma carta de vendas curta: manchete
          grande e centrada, frases de uma linha com o que importa em negrito,
          e a escolha da habilitação logo abaixo como o "clique aqui". É o
          momento de corrigir a crença que trava o funil — "primeiro estudo
          tudo, depois faço questões". Ela chega de um anúncio e talvez nem
          tenha lido a seção de método da landing. O número (75% contra 56%)
          fica para o resultado e para a landing. Quem já fez simulado não
          recebe cabeçalho nenhum: a carta é para quem ainda não começou, e
          "Olá, fulano · 2 simulados" só empurrava o painel para baixo. */}
      {estreante && (
        <section
          aria-label="Como começar"
          className="revela relative overflow-hidden rounded-2xl border border-navy-100 bg-white p-7 shadow-sm sm:p-10"
        >
          <RosaDosVentos grande />
          <div className="relative max-w-2xl">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">Sua preparação</p>
            <p className="mt-5 text-lg text-navy-900">Olá, {me.usuario.primeiro_nome}.</p>
            <h1 className="mt-2 font-display text-3xl leading-tight text-navy-900 sm:text-4xl">
              Não estude antes do diagnóstico.
            </h1>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-slate-700">
              <p>Quem começa pelas questões aprende mais do que quem começa pela apostila.</p>
              <p className="font-semibold text-navy-900">Reprovar no primeiro é o esperado.</p>
              <p>O diagnóstico é grátis, leva poucos minutos e mostra exatamente o que ainda falta estudar.</p>
              <p>
                Aí, sim, estude só isso e{" "}
                <span className="font-semibold text-navy-900">marque a prova sabendo que vai passar.</span>
              </p>
            </div>
            <p className="mt-7 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">
              Escolha a habilitação abaixo e comece
            </p>
          </div>
        </section>
      )}

      {erro && <Erro mensagem={erro} />}

      {/* Card fixo e proeminente, nem popup nem redirect: comunica a urgência
          do prazo e mantém o controle com o candidato. */}
      {aberta && (
        <div className="revela rounded-2xl border border-gold-500 bg-gold-100/50 p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-1">
              <p className="font-semibold text-navy-900">Simulado em andamento</p>
              <p className="text-sm text-navy-700">
                {aberta.prova} · questão {Math.min(aberta.respondidas + 1, aberta.total)} de {aberta.total}
                {aberta.cronometrada && ` · termina em ${cronometro(aberta.segundos_restantes)}`}
              </p>
              <div className="w-56 pt-1">
                <Barra atual={aberta.respondidas} total={aberta.total} />
              </div>
            </div>
            <Botao onClick={() => navegar(`/simulado/${aberta.id}`)}>Continuar</Botao>
          </div>
        </div>
      )}

      {/* Quem nunca fez um simulado não precisa de boletim — precisa de empurrão.
          Some para sempre depois da primeira tentativa concluída. */}
      {estreante && !aberta && <PrimeiroSimulado />}

      {!habilitacoes ? (
        <Carregando texto="Carregando habilitações…" />
      ) : (
        <section aria-label="Suas habilitações" className="space-y-4">
          <h2
            className="revela font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600"
            style={{ "--atraso": "80ms" } as CSSProperties}
          >
            {estreante ? "Escolha a habilitação" : "Suas habilitações"}
          </h2>

          <div className="grid gap-5 md:grid-cols-2">
            {ordenadas.map((habilitacao, i) => (
              <CartaoDaHabilitacao
                key={habilitacao.slug}
                habilitacao={habilitacao}
                atraso={120 + i * 90}
                iniciando={iniciando}
                bloqueado={aberta !== null}
                aoComecar={comecar}
                aoPreparar={(slug) => navegar(`/acesso?habilitacao=${slug}`)}
                aoVisualizar={(id) => navegar(`/simulado/${id}/resultado`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* O plano de estudo até a prova, para quem tem a preparação completa:
          a data, e o dia a dia a partir dela. */}
      {habilitacoes && <Plano habilitacoes={habilitacoes} recarregar={recarregar} />}

      {/* Os guias em PDF da preparação completa, para quem a tem; uma linha de
          convite para quem já fez simulado e não tem. */}
      <Materiais mostrarConvite={!estreante} />

      {/* Os "100 números que caem", para quem tem a preparação completa. */}
      <Flashcards />

      {/* Boletim acumulado, um por habilitação — nota e programa não se
          comparam entre elas. Cada um decide não aparecer sem simulado no
          formato da prova. */}
      {ordenadas.map((habilitacao) => (
        <div key={habilitacao.slug} className="revela" style={{ "--atraso": "300ms" } as CSSProperties}>
          <Desempenho slug={habilitacao.slug} nome={habilitacao.nome} />
        </div>
      ))}

      {/* Só para quem tem simulado a rever: o diagnóstico não conta. */}
      {me.simulados_concluidos > 0 && (
        <p className="text-center text-sm text-slate-500">
          <Link to="/historico" className="font-semibold text-navy-800 underline underline-offset-4">
            Ver todos os simulados que você já fez
          </Link>
        </p>
      )}
    </div>
  )
}

/**
 * O card de uma habilitação, na linguagem da landing: brasão redondo, nome em
 * display, área em mono e a régua com os números oficiais do exame. Depois,
 * uma ação só — a que faz sentido agora:
 *   - diagnóstico ainda disponível e sem acesso → começar o diagnóstico;
 *   - diagnóstico feito e sem acesso → VISUALIZAR o diagnóstico. A página de
 *     resultado dele é a página de vendas; ninguém deveria comprar sem passar
 *     por ela, então o painel não manda para o checkout;
 *   - acesso que venceu → renovar, que abre a página de renovação com o
 *     progresso da pessoa e o preço de renovação — a narrativa de "começar"
 *     não serve para quem já construiu histórico aqui;
 *   - sem nada disso (nunca teve acesso nem fez diagnóstico — raro) → começar
 *     a preparação, que é o checkout;
 *   - com acesso → prontidão e "continuar a preparação", que é o formato da
 *     prova. O diagnóstico fica como ação secundária: quando ele era o botão
 *     primário, quem tinha acabado de pagar fazia só ele.
 */
function CartaoDaHabilitacao({
  habilitacao,
  atraso,
  iniciando,
  bloqueado,
  aoComecar,
  aoPreparar,
  aoVisualizar,
}: {
  habilitacao: HabilitacaoComProvas
  atraso: number
  iniciando: number | null
  bloqueado: boolean
  aoComecar: (prova: Prova) => void
  aoPreparar: (slug: string) => void
  aoVisualizar: (id: number) => void
}) {
  const temAcesso = habilitacao.acesso.vigente
  const gratis = !temAcesso && habilitacao.acesso.gratis_disponivel
  // Com acesso, o formato da prova vem primeiro e é o primário. Sem acesso e
  // com o grátis de pé, só a curta aparece.
  const visiveis = temAcesso
    ? [...habilitacao.provas].sort((a, b) => Number(b.modo === "oficial") - Number(a.modo === "oficial"))
    : gratis
      ? habilitacao.provas.filter((prova) => prova.gratis)
      : []
  const oficial = habilitacao.provas[0]?.oficial
  // Uma ação só vira bloco de largura inteira; duas viram linhas.
  const unica = visiveis.length <= 1

  return (
    <article
      className="revela group relative overflow-hidden rounded-2xl border border-navy-100 bg-white p-6 shadow-sm transition duration-300 hover:border-gold-500/60 sm:p-7"
      style={{ "--atraso": `${atraso}ms` } as CSSProperties}
    >
      <RosaDosVentos />

      <div className="relative flex items-center gap-4">
        {habilitacao.brasao && (
          <img
            src={habilitacao.brasao}
            alt=""
            aria-hidden="true"
            className="h-16 w-16 flex-none rounded-full object-cover ring-1 ring-gold-500/40 transition duration-500 group-hover:ring-gold-500"
          />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-2xl leading-none text-navy-900">{habilitacao.nome}</h3>
          <p className="mt-1.5 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-navy-400">
            {habilitacao.area_navegacao}
          </p>
        </div>
        <SituacaoDoAcesso acesso={habilitacao.acesso} slug={habilitacao.slug} />
      </div>

      {/* Os números OFICIAIS do exame, lidos do banco — a tabela que o
          candidato veio conferir, no mesmo desenho da landing. */}
      {oficial && (
        <dl className="relative mt-6 grid grid-cols-3 gap-3 border-y border-navy-100 py-4">
          {[
            [String(oficial.questoes), "questões"],
            [oficial.duracao_minutos != null ? duracao(oficial.duracao_minutos) : "—", "de prova"],
            [String(oficial.acertos_para_aprovar), "para aprovar"],
          ].map(([valor, rotulo]) => (
            <div key={rotulo}>
              <dt className="font-mono text-xl font-medium tabular-nums text-navy-900">{valor}</dt>
              <dd className="mt-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-navy-400">{rotulo}</dd>
            </div>
          ))}
        </dl>
      )}

      {temAcesso && (
        <div className="relative mt-5">
          <BlocoDeProntidao prontidao={habilitacao.prontidao} />
        </div>
      )}

      {/* Pix gerado e ainda no prazo: lembra e leva de volta ao QR. Quem gera o
          Pix num navegador embutido de app e troca para o banco volta com a
          página recarregada — sem isto, a saída era gerar outro. */}
      {!temAcesso && habilitacao.acesso.pix_pendente && (
        <div className="relative mt-5 rounded-xl border border-gold-500/60 bg-gold-500/10 p-4">
          <p className="text-sm font-semibold text-navy-900">
            Você tem um Pix de {reais(habilitacao.acesso.pix_pendente.valor_centavos)} aguardando pagamento
            {habilitacao.acesso.pix_pendente.vence_em && <>, válido até {dataCurta(habilitacao.acesso.pix_pendente.vence_em)}</>}.
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Pague no app do seu banco e o acesso libera sozinho. O código continua o mesmo.
          </p>
          <Botao className="mt-3 w-full" onClick={() => aoPreparar(habilitacao.slug)}>
            Ver meu Pix
          </Botao>
        </div>
      )}

      <div className="relative mt-5 divide-y divide-navy-100">
        {/* Sem acesso e sem grátis: uma ação só. Volta ao diagnóstico se houver
            um — é lá que mora a oferta —; senão, o checkout. */}
        {visiveis.length === 0 &&
          (habilitacao.acesso.expirado ? (
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-semibold text-navy-900">Sua preparação venceu</p>
                <p className="text-sm text-slate-600">Seu histórico e sua evolução continuam aqui. Renove e siga de onde parou.</p>
              </div>
              <Botao className="w-full" onClick={() => aoPreparar(habilitacao.slug)}>
                Renovar preparação
              </Botao>
            </div>
          ) : habilitacao.diagnostico_id !== null ? (
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-semibold text-navy-900">Diagnóstico feito</p>
                <p className="text-sm text-slate-600">Seu resultado, o que falta estudar e o caminho até a prova.</p>
              </div>
              <Botao className="w-full" onClick={() => aoVisualizar(habilitacao.diagnostico_id as number)}>
                Visualizar diagnóstico
              </Botao>
            </div>
          ) : (
            <Botao className="w-full" onClick={() => aoPreparar(habilitacao.slug)}>
              Começar minha preparação
            </Botao>
          ))}

        {visiveis.map((prova) => {
          const rotulo = prova.modo === "curta" ? (prova.gratis ? "Diagnóstico grátis" : "Diagnóstico") : "Formato da prova"
          const detalhe = `${prova.n_questoes} questões${prova.cronometrada && prova.duracao_minutos != null ? ` em ${duracao(prova.duracao_minutos)}, cronometrado` : " · sem cronômetro"}`
          return (
            <div
              key={prova.id}
              className={`flex gap-3 py-3 first:pt-0 last:pb-0 ${
                unica ? "flex-col" : "flex-wrap items-center justify-between"
              }`}
            >
              <div>
                <p className="text-sm font-semibold text-navy-900">{rotulo}</p>
                <p className="text-sm text-slate-600">{detalhe}</p>
              </div>
              {prova.liberada ? (
                <Botao
                  className={unica ? "w-full" : ""}
                  variante={prova.modo === "oficial" || unica ? "primario" : "fantasma"}
                  onClick={() => aoComecar(prova)}
                  disabled={iniciando !== null || bloqueado}
                  title={bloqueado ? "Termine o simulado em andamento antes de começar outro." : undefined}
                >
                  {iniciando === prova.id
                    ? "Montando…"
                    : prova.modo === "curta"
                      ? "Começar o diagnóstico"
                      : "Continuar preparação"}
                </Botao>
              ) : (
                <Botao className={unica ? "w-full" : ""} onClick={() => aoPreparar(habilitacao.slug)}>
                  Começar minha preparação
                </Botao>
              )}
            </div>
          )
        })}
      </div>

      {/* Uma linha, não um card: avisa para o pedido no fim não virar surpresa,
          sem transformar a entrada numa tarefa. O simulado abre normalmente —
          só o gabarito espera a confirmação. */}
      {habilitacao.acesso.confirmar_para_gabarito && (
        <p className="relative mt-4 text-xs text-slate-500">
          Para ver o gabarito comentado no fim, confirme seu e-mail —{" "}
          <a href="/confirmar/new" className="font-semibold text-navy-700 underline">
            reenviar o link
          </a>
          .
        </p>
      )}
    </article>
  )
}

/** "90 min" vira "1h30", como na landing e na prova. */
function duracao(minutos: number): string {
  if (minutos < 60) return `${minutos} min`
  const h = Math.floor(minutos / 60)
  const m = minutos % 60
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`
}

/**
 * Rosa dos ventos de fundo, quase invisível, cortada pelo canto do card. É a
 * mesma marca da landing, aqui como textura e não como ilustração.
 */
function RosaDosVentos({ grande = false }: { grande?: boolean }) {
  const raios = Array.from({ length: 16 }, (_, i) => (i * 360) / 16)
  const posicao = grande
    ? "-right-24 top-1/2 h-[28rem] w-[28rem] -translate-y-1/2 opacity-[0.05]"
    : "-right-14 -top-14 h-48 w-48 opacity-[0.045]"
  return (
    <svg
      viewBox="0 0 200 200"
      aria-hidden="true"
      focusable="false"
      className={`pointer-events-none absolute text-navy-900 ${posicao}`}
      fill="none"
      stroke="currentColor"
    >
      <circle cx="100" cy="100" r="96" strokeWidth="0.8" />
      <circle cx="100" cy="100" r="72" strokeWidth="0.6" />
      <circle cx="100" cy="100" r="34" strokeWidth="0.8" />
      {raios.map((a) => (
        <line
          key={a}
          x1="100"
          y1="100"
          x2={100 + 96 * Math.cos((a * Math.PI) / 180)}
          y2={100 + 96 * Math.sin((a * Math.PI) / 180)}
          strokeWidth={a % 90 === 0 ? 1.2 : a % 45 === 0 ? 0.8 : 0.4}
        />
      ))}
    </svg>
  )
}

/**
 * Situação do acesso da habilitação. Só aparece com acesso vigente, onde
 * carrega informação de verdade — os dias que restam. Perto do fim vira
 * aviso, porque renovar ESTENDE o prazo em vez de reiniciar: avisar cedo
 * não custa dia nenhum a ninguém. Sem acesso, o botão do card já diz tudo.
 */
function SituacaoDoAcesso({ acesso, slug }: { acesso: AcessoDaHabilitacao; slug: string }) {
  if (!acesso.vigente) return null

  const dias = acesso.dias_restantes ?? 0
  const acabando = dias <= DIAS_PARA_AVISAR

  return (
    <div className="text-right">
      <p className={`font-mono text-[0.62rem] uppercase tracking-wider ${acabando ? "text-red-700" : "text-emerald-700"}`}>
        {acabando ? "Vence em breve" : "Acesso liberado"}
      </p>
      <p className="mt-1 text-xs text-slate-600">
        {dias === 1 ? "resta 1 dia" : `restam ${dias} dias`}
        {acabando && (
          <>
            {" · "}
            <Link to={`/acesso?habilitacao=${slug}`} className="font-semibold text-navy-800 underline">
              renovar
            </Link>
          </>
        )}
      </p>
    </div>
  )
}

/**
 * Orientação da primeira sessão em três passos, como uma régua e não como um
 * card: é contexto, não ação. A linha dourada entre os números é a mesma que
 * a landing usa nas suas seções numeradas.
 */
function PrimeiroSimulado() {
  const passos: [string, string][] = [
    ["Escolha a habilitação", "Vai pilotar barco? Faça o Arrais — ele já dispensa a prova de Motonauta. Só moto aquática? Motonauta."],
    ["Faça o diagnóstico grátis", "Poucas questões, sem cronômetro. Você termina quando quiser, em até 24 horas."],
    ["Veja o que ainda falta estudar", "Acerto por unidade e o gabarito comentado, com a norma citada em cada questão."],
  ]

  return (
    <ol className="revela grid gap-6 sm:grid-cols-3 sm:gap-6" style={{ "--atraso": "40ms" } as CSSProperties}>
      {passos.map(([titulo, texto], i) => (
        <li
          key={titulo}
          className="relative sm:after:absolute sm:after:left-11 sm:after:top-4 sm:after:h-px sm:after:w-[calc(100%-2.75rem)] sm:after:bg-gold-500/40 sm:last:after:hidden"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-500/60 bg-white font-mono text-xs font-semibold text-gold-600">
            {String(i + 1).padStart(2, "0")}
          </span>
          <h3 className="mt-3 text-sm font-semibold text-navy-900">{titulo}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{texto}</p>
        </li>
      ))}
    </ol>
  )
}

/**
 * O estado final do produto, por habilitação: não preparado, em preparação,
 * pronto para a prova. Só simulados no formato da prova contam; a regra e a
 * nota mínima vêm do servidor (Simulados::Prontidao), a frase de
 * lib/prontidao — a mesma da tela de resultado.
 */
function BlocoDeProntidao({ prontidao }: { prontidao: Prontidao }) {
  const tom = prontidao.estado === "pronto" ? "ok" : prontidao.estado === "em_preparacao" ? "atencao" : "neutro"
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-navy-50/70 px-4 py-3">
      <Selo tom={tom}>{ROTULO[prontidao.estado]}</Selo>
      <p className="text-sm text-navy-800">{fraseDeProntidao(prontidao)}</p>
    </div>
  )
}
